import { BaseService } from '@/modules/core/app/services/abstracts/BaseService';
import Logger from '@/modules/core/app/helpers/Logger';

const CLIPBOARD_KEY = 'reportstudio-page-clipboard';
const CLIPBOARD_TTL_MS = 60 * 60 * 1000;

export class ReportStudioPageClipboardService extends BaseService {
  /**
   * Persist a copied page reference to localStorage so it can be pasted into another
   * report, including from a different browser tab on the same origin.
   *
   * @param {{ sourceReportId: string, sourcePageId: string, pageTitle: string }} payload
   * @returns {boolean} false if the write failed (e.g. localStorage quota exceeded).
   */
  copyPage({ sourceReportId, sourcePageId, pageTitle }) {
    const clipboard = {
      source_report_id: sourceReportId,
      source_page_id: sourcePageId,
      page_title: pageTitle,
      copied_at: Date.now(),
    };
    try {
      window.localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(clipboard));
      return true;
    } catch (e) {
      Logger.log(e);
      return false;
    }
  }

  /**
   * @returns {{ source_report_id: string, source_page_id: string, page_title: string }|null}
   */
  getCopiedPage() {
    try {
      const raw = window.localStorage.getItem(CLIPBOARD_KEY);
      if (!raw) {
        return null;
      }
      const clipboard = JSON.parse(raw);
      if (!clipboard?.source_report_id || !clipboard?.source_page_id) {
        return null;
      }
      if (Date.now() - (clipboard.copied_at ?? 0) > CLIPBOARD_TTL_MS) {
        this.clear();
        return null;
      }
      return clipboard;
    } catch (e) {
      Logger.log(e);
      return null;
    }
  }

  hasCopiedPage() {
    return this.getCopiedPage() !== null;
  }

  clear() {
    window.localStorage.removeItem(CLIPBOARD_KEY);
  }
}

export default new ReportStudioPageClipboardService();
